/**
 * REST クライアント（機能設計書 §5 / UI/UX設計書 §2）。
 * 401 を検知したら /auth/refresh でトークンを再発行し、元のリクエストを 1 回だけ再試行する。
 */
import { ApiError, type ApiErrorBody } from "./errors";
import type { TokenPair } from "./types";

export const API_BASE = import.meta.env.VITE_API_BASE ?? "/api/v1";

const ACCESS_KEY = "vmall.access_token";
const REFRESH_KEY = "vmall.refresh_token";

export const tokens = {
  get access() { return localStorage.getItem(ACCESS_KEY); },
  get refresh() { return localStorage.getItem(REFRESH_KEY); },
  set(pair: TokenPair) {
    localStorage.setItem(ACCESS_KEY, pair.access_token);
    localStorage.setItem(REFRESH_KEY, pair.refresh_token);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

type SessionExpiredHandler = () => void;
let onSessionExpired: SessionExpiredHandler = () => {};
export function setSessionExpiredHandler(fn: SessionExpiredHandler) { onSessionExpired = fn; }

let refreshing: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  const refresh = tokens.refresh;
  if (!refresh) return false;
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: refresh }),
        });
        if (!res.ok) { tokens.clear(); return false; }
        tokens.set((await res.json()) as TokenPair);
        return true;
      } catch {
        return false;
      } finally {
        setTimeout(() => { refreshing = null; }, 0);
      }
    })();
  }
  return refreshing;
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  headers?: Record<string, string>;
  auth?: boolean;      // 明示的に認証ヘッダを付けない場合は false
  retry?: boolean;     // 内部用
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, query, headers = {}, auth = true, retry = true } = opts;

  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
    }
  }

  const finalHeaders: Record<string, string> = { ...headers };
  const isFormData = body instanceof FormData;
  if (body !== undefined && !isFormData) finalHeaders["Content-Type"] = "application/json";
  const access = tokens.access;
  if (auth && access) finalHeaders.Authorization = `Bearer ${access}`;

  const res = await fetch(url.toString(), {
    method,
    headers: finalHeaders,
    body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
  });

  if (res.status === 401 && retry && tokens.refresh) {
    if (await refreshTokens()) {
      return request<T>(path, { ...opts, retry: false });
    }
    tokens.clear();
    onSessionExpired();
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const data = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const errBody: ApiErrorBody =
      data && typeof data === "object" && "code" in data
        ? (data as ApiErrorBody)
        : { code: "E5000", message: res.statusText };
    if (res.status === 401) onSessionExpired();
    throw new ApiError(res.status, errBody);
  }
  return data as T;
}

export const api = {
  get:  <T>(p: string, query?: RequestOptions["query"]) => request<T>(p, { query }),
  post: <T>(p: string, body?: unknown, headers?: Record<string, string>) =>
          request<T>(p, { method: "POST", body, headers }),
  put:  <T>(p: string, body?: unknown) => request<T>(p, { method: "PUT", body }),
  del:  <T>(p: string) => request<T>(p, { method: "DELETE" }),
};

/** WebSocket URL（機能設計書 §6：token はクエリパラメータで渡す） */
export function wsUrl(spaceId: number): string {
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  const base = import.meta.env.VITE_WS_BASE ?? `${proto}://${window.location.host}`;
  return `${base}/api/v1/ws/spaces/${spaceId}?token=${encodeURIComponent(tokens.access ?? "")}`;
}


/**
 * ページネーションされた一覧をすべて取得する。
 * 一覧 API は 1 回あたり最大 100 件（機能設計書 §10）なので、
 * 絞り込みの選択肢のように全件が必要な場面ではこれを使う。
 */
export async function fetchAll<T>(
  path: string,
  query: RequestOptions["query"] = {},
  limit = 100,
): Promise<T[]> {
  const items: T[] = [];
  for (let page = 1; ; page++) {
    const res = await api.get<{ total: number; items: T[] }>(path, { ...query, page, limit });
    items.push(...res.items);
    if (items.length >= res.total || res.items.length === 0) return items;
  }
}
